import { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../api';

const CATEGORY_COLORS = {
  Groceries: '#22c55e',
  Dining: '#f97316',
  Shopping: '#8b5cf6',
  Transportation: '#3b82f6',
  Utilities: '#eab308',
  Entertainment: '#ec4899',
  Healthcare: '#ef4444',
  Income: '#10b981',
  Transfer: '#6b7280',
  Subscription: '#06b6d4',
  Travel: '#f59e0b',
  Other: '#71717a',
};

function DonutChart({ segments, centerLabel, centerValue, size }) {
  const s = size || 120;
  const r = (s - 12) / 2;
  const cx = s / 2;
  const cy = s / 2;
  const circumference = 2 * Math.PI * r;
  const total = segments.reduce((sum, seg) => sum + seg.value, 0);

  let offset = 0;
  const arcs = segments.filter((seg) => seg.value > 0).map((seg) => {
    const pct = total > 0 ? seg.value / total : 0;
    const dashLength = pct * circumference;
    const dashOffset = -offset * circumference / total;
    const arc = { ...seg, dashLength, dashOffset, circumference };
    offset += seg.value;
    return arc;
  });

  return (
    <div className="wd-donut-container">
      <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="#22232d" strokeWidth="10" />
        {arcs.map((arc, i) => (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none"
            stroke={arc.color} strokeWidth="10"
            strokeDasharray={`${arc.dashLength} ${arc.circumference - arc.dashLength}`}
            strokeDashoffset={arc.dashOffset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        ))}
        <text x={cx} y={cy - 6} textAnchor="middle" fill="#e4e4e7" fontSize="18" fontWeight="700">
          {centerValue}
        </text>
        <text x={cx} y={cy + 12} textAnchor="middle" fill="#71717a" fontSize="10">
          {centerLabel}
        </text>
      </svg>
      <div className="wd-donut-legend">
        {segments.filter((s) => s.value > 0).map((seg, i) => (
          <span key={i} className="wd-legend-item">
            <span className="wd-legend-dot" style={{ background: seg.color }} />
            {seg.label}: ${Math.abs(seg.value).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </span>
        ))}
      </div>
    </div>
  );
}

function FinancialDashboard({ refreshKey, extraRefreshKey }) {
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [refreshKey, extraRefreshKey]);

  async function fetchData() {
    try {
      const [txRes, acctRes] = await Promise.all([
        apiFetch('/api/simplefin/transactions'),
        apiFetch('/api/simplefin/accounts'),
      ]);
      if (txRes.ok) setTransactions(await txRes.json());
      if (acctRes.ok) setAccounts(await acctRes.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const stats = useMemo(() => {
    const now = new Date();
    const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonth = `${lastMonthDate.getFullYear()}-${String(lastMonthDate.getMonth() + 1).padStart(2, '0')}`;

    let netWorth = 0;
    for (const acct of accounts) {
      netWorth += parseFloat(acct.balance) || 0;
    }

    const thisMonthTx = [];
    const lastMonthTx = [];
    for (const tx of transactions) {
      const date = tx.posted || tx.transactedAt;
      if (!date) continue;
      const month = date.slice(0, 7);
      if (month === thisMonth) thisMonthTx.push(tx);
      else if (month === lastMonth) lastMonthTx.push(tx);
    }

    // Spending = negative amounts (outflows), excluding transfers
    const spending = (txList) => txList
      .filter((t) => parseFloat(t.amount) < 0 && t.category !== 'Transfer' && t.category !== 'Income')
      .reduce((sum, t) => sum + Math.abs(parseFloat(t.amount)), 0);

    const income = (txList) => txList
      .filter((t) => parseFloat(t.amount) > 0 && t.category !== 'Transfer')
      .reduce((sum, t) => sum + parseFloat(t.amount), 0);

    const thisSpending = spending(thisMonthTx);
    const lastSpending = spending(lastMonthTx);
    const thisIncome = income(thisMonthTx);
    const lastIncome = income(lastMonthTx);

    // By category this month
    const byCategory = {};
    for (const tx of thisMonthTx) {
      const amt = parseFloat(tx.amount);
      if (amt >= 0 || tx.category === 'Transfer') continue;
      const cat = tx.category || 'Other';
      byCategory[cat] = (byCategory[cat] || 0) + Math.abs(amt);
    }

    // Controllable spending (budget-relevant)
    const controllableSpending = thisMonthTx
      .filter((t) => parseFloat(t.amount) < 0 && t.spendingType === 'controllable')
      .reduce((sum, t) => sum + Math.abs(parseFloat(t.amount)), 0);
    const lastControllable = lastMonthTx
      .filter((t) => parseFloat(t.amount) < 0 && t.spendingType === 'controllable')
      .reduce((sum, t) => sum + Math.abs(parseFloat(t.amount)), 0);

    // By spending type
    const bySpendingType = { bills: 0, controllable: 0, unplanned: 0, '': 0 };
    for (const tx of thisMonthTx) {
      const amt = parseFloat(tx.amount);
      if (amt >= 0 || tx.category === 'Transfer') continue;
      const st = tx.spendingType || '';
      bySpendingType[st] = (bySpendingType[st] || 0) + Math.abs(amt);
    }

    // Uncategorized count
    const uncategorized = thisMonthTx.filter((t) => !t.category && parseFloat(t.amount) < 0).length;

    // Top merchants this month
    const byMerchant = {};
    for (const tx of thisMonthTx) {
      const amt = parseFloat(tx.amount);
      if (amt >= 0) continue;
      const desc = tx.description || 'Unknown';
      byMerchant[desc] = (byMerchant[desc] || 0) + Math.abs(amt);
    }
    const topMerchants = Object.entries(byMerchant)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    return {
      netWorth,
      thisSpending,
      lastSpending,
      thisIncome,
      lastIncome,
      byCategory,
      uncategorized,
      topMerchants,
      txCount: thisMonthTx.length,
      controllableSpending,
      lastControllable,
      bySpendingType,
    };
  }, [transactions, accounts]);

  if (loading) {
    return (
      <section className="card">
        <h2>Financial Overview</h2>
        <p className="loading">Loading...</p>
      </section>
    );
  }

  function formatCurrency(val) {
    return val.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  }

  const spendingChange = stats.lastSpending > 0
    ? ((stats.thisSpending - stats.lastSpending) / stats.lastSpending * 100)
    : 0;

  const categorySegments = Object.entries(stats.byCategory)
    .sort((a, b) => b[1] - a[1])
    .map(([cat, val]) => ({
      label: cat,
      value: val,
      color: CATEGORY_COLORS[cat] || '#71717a',
    }));

  return (
    <section className="card fd-section">
      <h2>Financial Overview</h2>

      <div className="wd-stats-row">
        <div className="wd-stat-box">
          <span className="wd-stat-num">{formatCurrency(stats.netWorth)}</span>
          <span className="wd-stat-label">Net Worth</span>
        </div>
        <div className="wd-stat-box">
          <span className="wd-stat-num" style={{ color: '#ef4444' }}>{formatCurrency(stats.thisSpending)}</span>
          <span className="wd-stat-label">Spending (MTD)</span>
        </div>
        <div className="wd-stat-box">
          <span className="wd-stat-num" style={{ color: '#22c55e' }}>{formatCurrency(stats.thisIncome)}</span>
          <span className="wd-stat-label">Income (MTD)</span>
        </div>
        <div className="wd-stat-box">
          <span className="wd-stat-num" style={{ color: '#f59e0b' }}>{formatCurrency(stats.controllableSpending)}</span>
          <span className="wd-stat-label">Controllable</span>
        </div>
      </div>

      <div className="wd-charts-row">
        <div className="wd-chart-col">
          <h3>Spending by Category</h3>
          {categorySegments.length > 0 ? (
            <DonutChart
              segments={categorySegments}
              centerLabel="this month"
              centerValue={formatCurrency(stats.thisSpending)}
              size={140}
            />
          ) : (
            <p style={{ color: '#71717a', fontSize: 13 }}>No categorized spending yet</p>
          )}
        </div>

        <div className="wd-chart-col">
          <h3>vs Last Month</h3>
          <div className="fd-comparison">
            <div className="fd-compare-row">
              <span className="fd-compare-label">Spending</span>
              <span className={`fd-compare-value ${spendingChange > 0 ? 'negative' : 'positive'}`}>
                {formatCurrency(stats.thisSpending)} vs {formatCurrency(stats.lastSpending)}
                {stats.lastSpending > 0 && (
                  <span className="fd-compare-pct">
                    {' '}({spendingChange > 0 ? '+' : ''}{spendingChange.toFixed(0)}%)
                  </span>
                )}
              </span>
            </div>
            <div className="fd-compare-row">
              <span className="fd-compare-label">Income</span>
              <span className="fd-compare-value positive">
                {formatCurrency(stats.thisIncome)} vs {formatCurrency(stats.lastIncome)}
              </span>
            </div>
            <div className="fd-compare-row">
              <span className="fd-compare-label">Controllable</span>
              <span className="fd-compare-value" style={{ color: '#f59e0b' }}>
                {formatCurrency(stats.controllableSpending)} vs {formatCurrency(stats.lastControllable)}
              </span>
            </div>
            <div className="fd-compare-row" style={{ borderBottom: 'none', paddingBottom: 0 }}>
              <span className="fd-compare-label" style={{ fontSize: 11, color: '#52525b' }}>Spending Breakdown</span>
            </div>
            {[
              { key: 'bills', label: 'Normal Bills', color: '#3b82f6' },
              { key: 'controllable', label: 'Controllable', color: '#f59e0b' },
              { key: 'unplanned', label: 'Unplanned', color: '#ef4444' },
              { key: '', label: 'Untyped', color: '#71717a' },
            ].filter(t => (stats.bySpendingType[t.key] || 0) > 0).map(t => (
              <div key={t.key} className="fd-compare-row" style={{ paddingLeft: 12 }}>
                <span className="fd-compare-label" style={{ color: t.color }}>{t.label}</span>
                <span className="fd-compare-value" style={{ color: t.color }}>{formatCurrency(stats.bySpendingType[t.key] || 0)}</span>
              </div>
            ))}
            {stats.uncategorized > 0 && (
              <div className="fd-compare-row">
                <span className="fd-compare-label" style={{ color: '#fbbf24' }}>Uncategorized</span>
                <span className="fd-compare-value" style={{ color: '#fbbf24' }}>{stats.uncategorized} transactions</span>
              </div>
            )}
          </div>

          <h3 style={{ marginTop: 16 }}>Top Merchants</h3>
          <div className="fd-merchants">
            {stats.topMerchants.map(([name, amt]) => (
              <div key={name} className="fd-merchant-row">
                <span className="fd-merchant-name">{name}</span>
                <span className="fd-merchant-amt">{formatCurrency(amt)}</span>
              </div>
            ))}
            {stats.topMerchants.length === 0 && (
              <p style={{ color: '#71717a', fontSize: 13 }}>No spending data</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export default FinancialDashboard;
